import { useCallback, useState } from "react";
import { Button } from "slate-ui-components";
import styled from "styled-components";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { useIntl } from "react-intl";
import { optionToggle, VisualizerSlice } from "../store/reducers/visualizer";
import { ZLVEVisualizerState } from "../types/VisualizerTypes";
import { JSONEquality } from "../utils/equal";


interface HotspotProps {
	info: {
		animation: string;
		animation_state?: "INDETERMINATE" | "CLOSED" | "OPENED" | "CLOSING" | "OPENING"
		viewport_pos: {
			x: number;
			y: number;
		};
		id: string;
		depth: number;
		occlusion: number;
		viewing_angle: number;
		cam_dir_point_dir_dotp: number;
		nodeHasValidAnimMesh: boolean;
		exterior: boolean;
		data?: unknown;
	}
}

const HotspotButton = styled(Button)`
	@media (pointer: coarse) {
		margin: -15px;
		padding: 15px;
	}
	pointer-events: auto;

	&:hover:not(:disabled)>div, &:focus:not(:disabled)>div {
		background-color: ${({theme}) => theme.colours.primary.hover};
	}

	&:disabled {
		opacity: 0;
		pointer-events: none;
	}
	transition: opacity 0.2s linear;
`

const HotspotIcon = styled.div`
	width: 17px;
	height: 17px;
	border-radius: 50%;
	border: 3px solid ${({theme}) => theme.colours.background.normal};
	background-color: ${({theme}) => theme.colours.primary.normal};
	
`

export const Hotspot : React.FC<HotspotProps> = ({info}) => {
	const intl = useIntl()
	const dispatch = useAppDispatch()
	const [hidden, setHidden] = useState(false)
	const state = useAppSelector(s => s.configuration.selectedOptions.animations.includes(info.animation || info.id))
	const { camera, prcodes } = useAppSelector((s) => VisualizerSlice.getSelectors().visualiserState(s.configuration) as ZLVEVisualizerState, JSONEquality)

	const onClick = useCallback(() => {
		dispatch(optionToggle({group: "animations", option: info.animation || info.id}))
		setHidden(true)
		setTimeout(() => setHidden(false), 2000)
	}, [dispatch, info.animation, info.id])

	if(!info.animation || info.id === "All") {
		return null;
	}

	if(camera.startsWith("Ext_") !== info.exterior) {
		return null;
	}

	if(info.id !== "Headlights" && !info.nodeHasValidAnimMesh) {
		return null;
	}

	if(info.id === "Trunk" && prcodes.includes("TB009-Fastback")) {
		if(state) {
			dispatch(optionToggle({group: "animations", option: info.animation || info.id}))
		}
		return null;
	}

	const animating = info.animation_state === "OPENING" || info.animation_state === "CLOSING";

	// eslint-disable-next-line formatjs/enforce-default-message
	const label = intl.formatMessage({id: `hotspot.${info.id}.name`, defaultMessage: ""}) ?? ""

	return (
		<HotspotButton label={label} testId={`hotspot.${info.id}`} onClick={onClick} disabled={hidden || animating || info.viewing_angle < 90}>
			<HotspotIcon/>
		</HotspotButton>
	)
}